import React, { useState } from 'react';
import { Row, Col } from '../Grid';
import { RSInput, RSButton } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import './styles.css';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import IdentityProviderButton from '../auth/IdentityProviderButton';
import queryString from 'query-string';
import { isEmpty, pick } from 'lodash';
import Styles from '../../styles/colors';
import { useStores } from '../../hooks/useStores';

// reactsymbols-kit's RSInput extends React.HTMLProps, so its declared `onChange`
// is the unusable intersection of the DOM FormEventHandler with the kit's value
// callback. Route the real value handler through this helper, re-typed as the
// shape the prop wants (the single library-boundary escape for this kit bug;
// the runtime handler is unchanged).
type RSInputChange = React.ComponentProps<typeof RSInput>['onChange'];
function asRSChange<T>(handler: (...args: never[]) => void): T {
  return handler as unknown as T;
}

export const typeOptions = [
  {
    value: 'STUDENT',
    label: 'Student',
  },
  {
    value: 'PERSONAL',
    label: 'Personal Use',
  },
  {
    value: 'COMPANY',
    label: 'Company',
  },
  {
    value: 'OTHER',
    label: 'Other',
  },
];

/**
 * The account view on /account.
 *
 * Shows buttons to link/unlink identity providers and a form to update personal
 * details.
 *
 * The auth store was injected with mobx-react (`inject(({ store }) => ({ auth:
 * store.auth }))`); it now comes from useStores(). `location.search` (the query
 * string the backend redirects here with on a failed link/unlink) comes from
 * useLocation() instead of the withRouter shim.
 *
 * The form fields are seeded from `auth.user` at mount, and the `error` is
 * derived from the query string once — both via useState initializers (replacing
 * the constructor's `getState(props)`). Each field is its own useState slot so
 * that the per-field `setState({ firstname })` partial merges of the class map
 * to plain single-field setters with identical semantics.
 */
export default observer(function Account() {
  const store = useStores();
  const auth = store.auth;
  const location = useLocation();

  // The /account view only renders for a logged-in user, so `auth.user` is
  // always present here (the original read `auth.user.X` unguarded). The
  // non-null assertion keeps the same object reference — and the same MobX
  // tracking — without adding a runtime guard.
  const user = auth.user!;

  const [firstname, setFirstname] = useState(user.firstname);
  const [lastname, setLastname] = useState(user.lastname);
  const [email, setEmail] = useState(user.email);
  const [type, setType] = useState(user.type);
  const [affiliation, setAffiliation] = useState(user.affiliation);

  // The backend redirects here with `?error=...` after a failed link/unlink.
  // Derived from the query string once at mount (the class did this in
  // getState() called from the constructor). Read-only afterwards.
  const [error] = useState<string | (string | null)[] | undefined>(() => {
    const query = queryString.parse(location.search);
    return query.error ? query.error : undefined;
  });

  const values = { firstname, lastname, email, type, affiliation };

  // The mutable account fields, shared by the validity/changed checks and the
  // save handler. Typed so `values[field]` / `user[field]` index cleanly.
  type AccountField = keyof typeof values;

  /**
   * Are the account details valid?
   *
   * The type cannot be empty (it's a dropdown without empty state).
   */
  const valid = !isEmpty(firstname) && !isEmpty(lastname) && !isEmpty(email);

  /**
   * Did the account details change?
   */
  const changed = (['firstname', 'lastname', 'email', 'type', 'affiliation'] as AccountField[]).some(
    (key) => values[key] !== user[key]
  );

  const renderError = () => {
    if (error) {
      return (
        <Row gutter={30}>
          <Col xs={12}>
            <div className="error">{error}</div>
          </Col>
        </Row>
      );
    }
  };

  /**
   * Save the account details
   */
  const onSave = () => {
    const changedAttrs = (['firstname', 'lastname', 'email', 'type', 'affiliation'] as AccountField[]).filter(
      (key) => values[key] !== user[key]
    );
    const changes = pick(values, changedAttrs);
    auth.update(changes);
  };

  /**
   * Handle account details form submit
   */
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (valid && changed) {
      onSave();
    }
  };

  const renderIdentityProviderButtons = () => {
    const identities = user.identities.map((id) => id.provider);
    return (
      <Col md={6} xs={12}>
        <IdentityProviderButton
          method={identities.includes('kuleuven') ? 'unlink' : 'link'}
          provider="kuleuven"
        />
        <IdentityProviderButton
          method={identities.includes('google') ? 'unlink' : 'link'}
          provider="google"
        />
      </Col>
    );
  };

  const renderAccountDetailsForm = () => {
    return (
      <Col md={6} xs={12}>
        <form onSubmit={onSubmit}>
          <RSInput
            label="Firstname"
            value={firstname}
            onChange={asRSChange<RSInputChange>((value: string) => setFirstname(value))}
            autoFocus
          />
          <RSInput label="Lastname" value={lastname} onChange={asRSChange<RSInputChange>((value: string) => setLastname(value))} />
          <RSInput
            label="E-mail"
            value={email}
            onChange={asRSChange<RSInputChange>((value: string) => setEmail(value))}
            type="email"
          />
          <div className="rs-element rs-input-container rs-input">
            <label className="rs-element rs-form-label">Account Type</label>
            <MerlinSelect
              className="rs-input-wrap"
              label="Account type"
              options={typeOptions}
              value={type}
              onChange={(option: MerlinSelectOption | null) =>
                option != null && setType(option.value as typeof type)
              }
              searchable={false}
              clearable={false}
            />
          </div>
          <RSInput
            label="Company"
            // `affiliation` is nullable (User.affiliation is `maybeNull`); the
            // original forwarded the raw value (incl. null) to RSInput, whose
            // value prop is typed `string | ...`. Cast preserves the exact
            // runtime value while satisfying the prop type.
            value={affiliation as string}
            onChange={asRSChange<RSInputChange>((value: string) => setAffiliation(value))}
          />
          {/* The button doesn't need an onClick handler. The onSubmit also fires for buttons inside a form. */}
          <RSButton
            background={Styles.blueGradient}
            value="Change Profile"
            type="submit"
            disabled={!valid || !changed}
          />
        </form>
      </Col>
    );
  };

  return (
    <div className="account-wrapper mx-auto flex min-h-full max-w-[800px] flex-col items-center justify-center">
      {renderError()}
      <Row gutter={30}>
        {renderIdentityProviderButtons()}
        {renderAccountDetailsForm()}
      </Row>
    </div>
  );
});
